/**
 * Created by andrey on 07.12.2023
 */

const BucketsLocalStorage = function (localStorage, buckets = 128) {
    this.localStorage = localStorage || new HtmlLocalStorage();
    this.buckets = buckets;

    this.prefix = SimpleDataLoader.GetLocalStoragePrefix();

    this.data = {};
    this.dirty = new Map();

    this.queue = new Map();
    this.dirtyQueue = false;

    this.flush = connector.utils.accumulate(1000, () => this._flush());
};

BucketsLocalStorage.prototype.initialize = function (callback) {
    new ConnectorActionPlayer([
        (f) => {
            if (this.localStorage.initialize) {
                this.localStorage.initialize(f);
            } else {
                f();
            }
        },

        (f) => this._initialize(f)
    ]).play(callback);
};

BucketsLocalStorage.prototype._initialize = function (callback) {
    const start = Date.now();

    const keys = Array.from({ length: this.buckets }, (_, i) => this.getBucketForIndex(i));

    const promises = keys.map((bucketKey) => this.getLocalStorageItemAsync(bucketKey).then((value) => {
        this.data[bucketKey] = value;
    }));

    promises.push(this.loadQueueAsync());

    Promise.all(promises)
        .then(() => console.log(`[connector] local storage initialize ${Date.now() - start}ms`))
        .catch((e) => console.error("[connector] local storage initialize error", e))
        .finally(() => {
            this.migrateBuckets();
            connector.timeouts.setInterval(() => this._flush(), 1000);
            callback();
        });
};

BucketsLocalStorage.prototype.getItem = function (key) {
    if (this.queue.has(key)) {
        return this.queue.get(key);
    }

    const bucketKey = this.getBucketForKey(key);
    const bucket = this.data[bucketKey];
    return bucket && bucket[key];
};

BucketsLocalStorage.prototype.setItem = function (key, value) {
    this.queue.set(key, value);
    this.dirtyQueue = true;
};

BucketsLocalStorage.prototype.removeItem = function (key) {
    this.queue.set(key, undefined);
    this.dirtyQueue = true;
};

BucketsLocalStorage.prototype.getData = function (prefix = "") {
    return this.getAllKeys(prefix).reduce((data, key) => {
        const value = this.getItem(key);
        if (value !== undefined) {
            data[key] = value;
        }

        return data;
    }, {});
};

BucketsLocalStorage.prototype.clearData = function (prefix = "") {
    this.getAllKeys(prefix).forEach((key) => this.removeItem(key));
};

BucketsLocalStorage.prototype.getAllKeys = function (prefix = "") {
    const keys = Object.values(this.data).flatMap((bucketData) => Object.keys(bucketData))
        .concat([...this.queue.keys()])
        .filter((key) => key.startsWith(prefix));

    return [...new Set(keys)];
};

BucketsLocalStorage.prototype._flush = function () {
    if (this.flushing) {
        return;
    }

    this.flushing = true;

    this.processQueue();

    return Promise.resolve()
        .then(() => this.saveBucketsAsync())
        .then(() => this.saveQueueAsync())
        .finally(() => {
            this.flushing = false;
        });
};

BucketsLocalStorage.prototype.processQueue = function () {
    const needProcess = this.queue.size >= BucketsLocalStorage.PROCESS_QUEUE_THRESHOLD
        || (this.lastProcessQueueTime || 0) + BucketsLocalStorage.PROCESS_QUEUE_TIMEOUT < Date.now();

    if (!needProcess || this.queue.size === 0) {
        return;
    }

    this.lastProcessQueueTime = Date.now();

    for (const key of this.queue.keys()) {
        const bucketKey = this.getBucketForKey(key);
        this.data[bucketKey] = this.data[bucketKey] || {};
        this.data[bucketKey][key] = this.queue.get(key);
        this.dirty.set(bucketKey, true);
    }

    this.queue.clear();
    this.dirtyQueue = true;
};

BucketsLocalStorage.prototype.loadQueueAsync = function () {
    const key = this.getBucketForIndex("queue");

    return this.getLocalStorageItemAsync(key).then((value) => {
        for (const [k, v] of Object.entries(value)) {
            this.queue.set(k, v === null ? undefined : v);
        }
    });
};

BucketsLocalStorage.prototype.saveQueueAsync = function () {
    if (!this.dirtyQueue) {
        return Promise.resolve();
    }
    this.dirtyQueue = false;

    const entries = [...this.queue].map(([k, v]) => [k, v === undefined ? null : v]);
    
    const key = this.getBucketForIndex("queue");
    const value = JSON.stringify(Object.fromEntries(entries));

    return this.saveBatchAsync([{ key, value }]);
};

BucketsLocalStorage.prototype.saveBucketsAsync = function () {
    if (this.dirty.size === 0) {
        return Promise.resolve();
    }

    const batch = [...this.dirty.keys()].map((key) => ({
        key,
        value: JSON.stringify(this.data[key])
    }));
    this.dirty.clear();

    return this.saveBatchAsync(batch);
};

BucketsLocalStorage.prototype.saveBatchAsync = function (batch) {
    let retryDelay = 1000;

    const save = () => {
        const promises = batch.map(({ key, value }) => Promise.resolve()
            .then(() => this.setLocalStorageItemAsync(key, value))
            .then(() => true)
            .catch((e) => {
                console.error("[connector] local storage save error", e);
                return false;
            }));

        return Promise.all(promises).then((results) => {
            if (results.every(Boolean)) {
                return;
            }

            return new Promise((resolve) => {
                connector.timeouts.setTimeout(resolve, retryDelay);
                retryDelay = Math.min(retryDelay * 2, 60000);
            }).then(save);
        });
    };

    return save();
};

BucketsLocalStorage.prototype.getBucketForKey = function (key) {
    const index = (connector.utils.hashCode(key) % this.buckets + this.buckets) % this.buckets;
    return this.getBucketForIndex(index);
};

BucketsLocalStorage.prototype.getBucketForIndex = function (index) {
    return `${this.prefix}_bucket_${index}`;
};

BucketsLocalStorage.prototype.migrateBuckets = function () {
    for (const bucketKey of Object.keys(this.data)) {
        const bucket = this.data[bucketKey];
        for (const key of Object.keys(bucket)) {
            const actualBucketKey = this.getBucketForKey(key);
            if (actualBucketKey === bucketKey) {
                continue;
            }

            const value = bucket[key];
            delete bucket[key];
            this.dirty.set(bucketKey, true);

            this.setItem(key, value);
        }
    }
};

BucketsLocalStorage.prototype.getLocalStorageItemAsync = function (key) {
    const promise = this.localStorage.getItemAsync
        ? this.localStorage.getItemAsync(key)
        : Promise.resolve(this.localStorage.getItem(key));

    return promise.then((value) => {
        if (value) {
            try {
                return JSON.parse(value);
            } catch (e) {
                console.error(`[connector] cant parse ${key}`, e);
            }
        }

        return {};
    });
};

BucketsLocalStorage.prototype.setLocalStorageItemAsync = function (key, value) {
    if (this.localStorage.setItem) {
        this.localStorage.setItem(key, value);
        return Promise.resolve(true);
    }

    return this.localStorage.setItemAsync(key, value);
};

BucketsLocalStorage.PROCESS_QUEUE_THRESHOLD = 100;
BucketsLocalStorage.PROCESS_QUEUE_TIMEOUT = connector.utils.parseInterval("5 minutes");
